import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://sportyreplay.de';
const DEFAULT_DESCRIPTION =
  'Kaufe und verkaufe gebrauchte Sportartikel innerhalb deines Vereins. Regional, nachhaltig und günstig.';
/** Fallback-Sharing-Bild im Brand-Design (1200x630), erzeugt von deploy/generate-og-image.mjs. */
const DEFAULT_IMAGE = '/og-image.png';

interface SeoProps {
  title: string;
  description?: string;
  /** Pfad inkl. fuehrendem Slash fuer die Canonical-URL, z.B. "/products/29". Default: aktueller Pfad. */
  path?: string;
  /** Bild-URL (absolut oder relativ) fuer og:image. Default: Brand-Sharing-Bild. */
  image?: string;
  /** Seite von der Indexierung ausschliessen (z.B. Login, Register). */
  noindex?: boolean;
  type?: string;
  /** JSON-LD strukturierte Daten (Objekt oder Array) fuer Rich Results. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image,
  noindex = false,
  type = 'website',
  jsonLd,
}: SeoProps) => {
  const pathname = path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonical = SITE + pathname;
  const src = image || DEFAULT_IMAGE;
  const imageUrl = src.startsWith('http') ? src : SITE + src;
  // Abmessungen nur beim Default-Bild angeben - Produktbilder haben beliebige Groessen.
  const isDefaultImage = src === DEFAULT_IMAGE;
  const jsonLdList = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph (Facebook, WhatsApp, LinkedIn) */}
      <meta property="og:site_name" content="SportyReplay" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title} />
      {isDefaultImage && <meta property="og:image:width" content="1200" />}
      {isDefaultImage && <meta property="og:image:height" content="630" />}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLdList.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
